/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { useParams } from 'react-router-dom';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { EventStore } from '../../../../stores/application/EventStore';
import { StoreProvider } from '../../../../stores/StoreProvider';
import * as H from 'history';
import { EntryService } from '../../../../services/EntryService';
import { Entry, ENTRY_REVIEW_STATE } from '../../../../types/course/Entry';
import { EntriesReviewStore } from '../../../../stores/components/EntriesReviewStore';
import { MaterialCircularProgress } from '../../../../models/material/MaterialCircularProgress';

export class EntriesReviewViewModel extends ViewModel {
	private circularProgress = new MaterialCircularProgress();
	private circularProgressNewEntries = new MaterialCircularProgress();
	private circularProgressApproved = new MaterialCircularProgress();
	private circularProgressRejected = new MaterialCircularProgress();
	private routerParams: { courseId: string } = useParams();

	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly eventStore: EventStore;
	private readonly entriesReviewStore: EntriesReviewStore;
	private entryService: EntryService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.eventStore = storeProvider.eventStore;
		this.entryService = storeProvider.entryService;
		this.entriesReviewStore = storeProvider.entriesReviewStore;
		this.queryNewEntries(this.routerParams.courseId);
		this.queryApprovedEntries(this.routerParams.courseId);
		this.queryRejectedEntries(this.routerParams.courseId);
		this.queryNumberOfPagesNewEntries();
		this.queryNumberOfPagesApproved();
		this.queryNumberOfPagesRejected();
		this.circularProgress.setIsLoading(true);
	}

	public isLoading(): boolean {
		return this.circularProgress.isLoading;
	}

	public isNewEntriesLoading(): boolean {
		return this.circularProgressNewEntries.isLoading;
	}

	public isApprovedLoading(): boolean {
		return this.circularProgressApproved.isLoading;
	}

	public isRejectedLoading(): boolean {
		return this.circularProgressRejected.isLoading;
	}

	public getNewEntries(): Entry[] {
		return this.entriesReviewStore.getNewEntries();
	}

	private setNewEntries(entries: Entry[]): void {
		this.entriesReviewStore.setNewEntries(entries);
	}

	public getApprovedEntries(): Entry[] {
		return this.entriesReviewStore.getApprovedEntries();
	}

	private setApprovedEntries(entries: Entry[]): void {
		this.entriesReviewStore.setApprovedEntries(entries);
	}

	public getRejectedEntries(): Entry[] {
		return this.entriesReviewStore.getRejectedEntries();
	}

	private setRejectedEntries(entries: Entry[]): void {
		this.entriesReviewStore.setRejectedEntries(entries);
	}

	public getPageNumberNewEntries(): number {
		return this.entriesReviewStore.getPageNumberNewEntries();
	}

	public setPageNumberNewEntries(pageNumber: number): void {
		this.entriesReviewStore.setPageNumberNewEntries(pageNumber);
		this.queryNewEntries(this.routerParams.courseId);
	}

	public getPageNumberApproved(): number {
		return this.entriesReviewStore.getPageNumberApproved();
	}

	public setPageNumberApproved(pageNumber: number): void {
		this.entriesReviewStore.setPageNumberApproved(pageNumber);
		this.queryApprovedEntries(this.routerParams.courseId);
	}

	public getPageNumberRejected(): number {
		return this.entriesReviewStore.getPageNumberRejected();
	}

	public setPageNumberRejected(pageNumber: number): void {
		this.entriesReviewStore.setPageNumberRejected(pageNumber);
		this.queryRejectedEntries(this.routerParams.courseId);
	}

	public getNumberOfPagesNewEntries(): number {
		return this.entriesReviewStore.getNumberOfPagesNewEntries();
	}

	public setNumberOfPagesNewEntries(numberOfPages: number): void {
		this.entriesReviewStore.setNumberOfPagesNewEntries(numberOfPages);
	}

	public getNumberOfPagesApproved(): number {
		return this.entriesReviewStore.getNumberOfPagesApproved();
	}

	public setNumberOfPagesApproved(numberOfPages: number): void {
		this.entriesReviewStore.setNumberOfPagesApproved(numberOfPages);
	}

	public getNumberOfPagesRejected(): number {
		return this.entriesReviewStore.getNumberOfPagesRejected();
	}

	public setNumberOfPagesRejected(numberOfPages: number): void {
		this.entriesReviewStore.setNumberOfPagesRejected(numberOfPages);
	}

	public setEntry(entry?: Entry): void {
		this.entriesReviewStore.setEntry(entry);
	}

	public getEntry(): Entry | undefined {
		return this.entriesReviewStore.getEntry();
	}

	public isDetailView(): boolean {
		return this.entriesReviewStore.getEntry() !== undefined;
	}

	public closeDetailView(): void {
		this.entriesReviewStore.setEntry(undefined);
	}

	public updateReviewState(entryId: string, newState: ENTRY_REVIEW_STATE): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.entryService
			.updateReviewState(entryId, newState, authToken)
			.then(() => {
				this.queryNewEntries(this.routerParams.courseId);
				this.queryNumberOfPagesNewEntries();
				if (newState === ENTRY_REVIEW_STATE.APPROVED) {
					this.queryApprovedEntries(this.routerParams.courseId);
					this.queryNumberOfPagesApproved();
				} else {
					this.queryRejectedEntries(this.routerParams.courseId);
					this.queryNumberOfPagesRejected();
				}
			})
			.catch(() => {
				this.snackbarStore.showError('entries.update_review_error');
			});
	}

	private queryNewEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.getPageNumberNewEntries();
		this.circularProgressNewEntries.setIsLoading(true);

		return this.entryService
			.getNewEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.setNewEntries(entries);
				this.circularProgressNewEntries.setIsLoading(false);
				this.circularProgress.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private queryApprovedEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.getPageNumberApproved();
		this.circularProgressApproved.setIsLoading(true);

		return this.entryService
			.getApprovedEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.setApprovedEntries(entries);
				this.circularProgressApproved.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private queryRejectedEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.getPageNumberRejected();
		this.circularProgressRejected.setIsLoading(true);

		return this.entryService
			.getRejectedEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.setRejectedEntries(entries);
				this.circularProgressRejected.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private async queryNumberOfPagesNewEntries(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesNewEntries(courseId, authToken)
			.then((numberOfPages: number) => {
				this.setNumberOfPagesNewEntries(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}

	private async queryNumberOfPagesApproved(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesApproved(courseId, authToken)
			.then((numberOfPages: number) => {
				this.setNumberOfPagesApproved(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}

	private async queryNumberOfPagesRejected(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesRejected(courseId, authToken)
			.then((numberOfPages: number) => {
				this.setNumberOfPagesRejected(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}
}
